import { Dialog, Listbox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getCookie } from "cookies-next";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { Fragment, useEffect, useState } from "react";
import { useUser } from "../../store/session";

import { toast } from "@/shadcn/hooks/use-toast";
import dynamic from "next/dynamic";
import { isEmail, priorities, types } from "@/shadcn/lib/utils";
import FileUploader from "../FileUploader";

const Editor = dynamic(() => import("../BlockEditor"), { ssr: false });

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export function ErrorList({ errors }: { errors: Record<string, string> }) {
    if (!errors || Object.keys(errors).length === 0) return null;
    return (
        <ul className="px-4 py-2 mb-2 text-xs text-left text-white bg-red-600 rounded">
            {Object.values(errors).map((msg, idx) => (
                <li key={idx}>{msg}</li>
            ))}
        </ul>
    );
}

export default function CreateTicketModal({ keypress, setKeyPressDown, onClose }) {
    const { t, lang } = useTranslation("nextmark");
    const [open, setOpen] = useState(false);
    const [files, setFiles] = useState<any>([]);

    const router = useRouter();

    const token = getCookie("session");

    const { user } = useUser();

    const [name, setName] = useState("");
    const [client, setClient] = useState<any>();
    const [engineer, setEngineer] = useState<any>();
    const [email, setEmail] = useState("");
    const [issue, setIssue] = useState<any>();
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState(priorities[1]);
    const [options, setOptions] = useState<any>();
    const [users, setUsers] = useState<any>();
    const [selected, setSelected] = useState<any>(types[3]);
    const [clientMember, setClientMember] = useState<any>();
    const [readOnly, setReadOnly] = useState(false);
    const [errors, setErrors] = useState<{
        [x: string]: string;
    }>({});

    const onMemberChange = (value: any) => {
        setClientMember(value);
        if (value && value.id) {
            setName(value.name);
            setEmail(value.email);
            setReadOnly(true);
        } else {
            setName("");
            setEmail("");
            setReadOnly(false);
        }
    };

    const onClientChange = (value: any) => {
        setClient(value);
        if (value && value.members && value.members.length > 0) {
            onMemberChange(value.members[0]); // Auto-select the first member if available
        } else {
            onMemberChange(undefined); // Reset if no members are available
            toast({
                variant: "destructive",
                title: `Error`,
                description: "No members found for this client. Please add members to the client.",
            });
        }
    };

    const fetchClients = async () => {
        await fetch(`/api/v1/clients/all`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((res) => {
                if (res) {
                    setOptions(res.clients);
                    if (!res.clients || res.clients.length == 0) {
                        toast({
                            variant: "destructive",
                            title: `Error`,
                            description: "No clients found. Please create a client first.",
                        });
                    }
                }
            });
    };

    async function fetchUsers() {
        try {
            await fetch(`/api/v1/users/all`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((res) => res.json())
                .then((res) => {
                    if (res) {
                        // TODO: THINK ABOUT AUTO ASSIGN PREFERENCES
                        setEngineer(user);
                        setUsers(res.users);
                    }
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function createTicket() {
        let newErrors: {
            [x: string]: string;
        } = {};
        if (!hideName && (!name || name === "" || name.length < 3)) {
            newErrors.name = "Name is required and must be at least 3 characters long";
        }
        if (!hideEmail && !isEmail(email)) {
            newErrors.email = "Email is required and must be a valid email address";
        }

        if (!client || client == "") {
            newErrors.client = "Client is required and must be selected";
        }

        if (!title || title === "" || title.length < 10) {
            newErrors.title = "Issue title is required and must be at least 10 characters long";
        }

        if (!engineer || engineer === "") {
            newErrors.engineer = "Engineer is required and must be selected";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        const formData = new FormData();
        formData.append("name", name);
        formData.append("title", title);
        formData.append("client", typeof client === "object" ? client.id : client);
        formData.append("email", email);
        formData.append("detail", JSON.stringify(issue || ""));
        formData.append("priority", priority.id);
        formData.append("engineer", typeof engineer === "object" ? engineer.id : engineer);
        formData.append("type", selected.name);
        formData.append("clientMember", typeof clientMember === "object" ? clientMember.id : clientMember);
        formData.append(
            "createdBy",
            JSON.stringify({
                id: user.id,
                name: user.name,
                role: user.role,
                email: user.email,
            })
        );

        // Append files
        if (files && files.length) {
            files.forEach((file: File, idx: number) => {
                formData.append("files", file);
            });
        }

        await fetch(`/api/v1/ticket/create/public`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        })
            .then((res) => res.json())
            .then((res) => {
                if (res.success === true) {
                    toast({
                        variant: "default",
                        title: "Success",
                        description: "Ticket created successfully",
                    });
                    setOpen(false);
                    setFiles([]);
                    setName("");
                    setEmail("");
                    setIssue("");
                    setTitle("");
                    setClient(undefined);
                    setEngineer(undefined);
                    setClientMember(undefined);
                    setSelected(types[3]);
                    setPriority(priorities[1]);
                    setReadOnly(false);
                    setErrors({});
                    router.push("/issues");
                } else {
                    toast({
                        variant: "destructive",
                        title: `Error`,
                        description: res.error,
                    });
                }
            });
    }

    function checkPress() {
        if (keypress) {
            setOpen(true);
            setKeyPressDown(false);
        }
    }

    useEffect(() => {
        fetchClients();
        fetchUsers();
    }, []);

    useEffect(() => checkPress(), [keypress]);

    const [hideKeyboardShortcuts, setHideKeyboardShortcuts] = useState(false);
    const [hideName, setHideName] = useState(false);
    const [hideEmail, setHideEmail] = useState(false);

    useEffect(() => {
        const loadFlags = () => {
            const savedFlags = localStorage.getItem("featureFlags");
            if (savedFlags) {
                const flags = JSON.parse(savedFlags);
                const hideShortcuts = flags.find((f: any) => f.name === "Hide Keyboard Shortcuts")?.enabled;

                const hideName = flags.find((f: any) => f.name === "Hide Name in Create")?.enabled;

                const hideEmail = flags.find((f: any) => f.name === "Hide Email in Create")?.enabled;

                setHideKeyboardShortcuts(hideShortcuts || false);
                setHideName(hideName || false);
                setHideEmail(hideEmail || false);
            }
        };

        loadFlags();
        window.addEventListener("storage", loadFlags);
        return () => window.removeEventListener("storage", loadFlags);
    }, []);

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-10" onClose={setOpen}>
                    <div className="flex items-end justify-center min-h-screen align-middle pt-4 mx-4 md:mx-12 text-center -mt-[50%] sm:-mt-0 sm:block sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0">
                            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                        </Transition.Child>

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <div className="inline-block w-full px-4 pt-5 pb-4 text-left align-middle transition-all transform rounded-lg shadow-xl bg-background sm:my-8 md:max-w-3xl max-h-[90vh] overflow-y-auto">
                                <div className="flex flex-row w-full align-middle">
                                    <span className="pb-2 text-sm font-semibold text-md">New Issue</span>

                                    <button
                                        type="button"
                                        className="ml-auto mb-1.5 text-foreground font-bold text-xs rounded-md hover:text-primary outline-none"
                                        onClick={() => {
                                            setOpen(false), onClose();
                                        }}>
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                                    </button>
                                </div>
                                <ErrorList errors={errors} />
                                <div className="flex flex-col space-y-2">
                                    <div className="flex flex-col gap-2 pt-4 pb-2">
                                        {!user.external_user && (
                                            <>
                                                <div className="flex flex-row space-x-4">
                                                    <Listbox value={client} onChange={onClientChange}>
                                                        {({ open }) => (
                                                            <>
                                                                <div className="relative">
                                                                    <Listbox.Button className="relative w-full min-w-[172px] cursor-default rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                                                                        <span className="block truncate">
                                                                            {client === undefined ? t("select_a_client") : client === "" ? t("select_a_client") : client.name}
                                                                        </span>
                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                                        </span>
                                                                    </Listbox.Button>

                                                                    <Transition
                                                                        show={open}
                                                                        as={Fragment}
                                                                        leave="transition ease-in duration-100"
                                                                        leaveFrom="opacity-100"
                                                                        leaveTo="opacity-0">
                                                                        <Listbox.Options className="absolute z-10  max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                            {options !== undefined &&
                                                                                options.map((client: any) => (
                                                                                    <Listbox.Option
                                                                                        key={client.id}
                                                                                        className={({ active }) =>
                                                                                            classNames(
                                                                                                active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                                                "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                            )
                                                                                        }
                                                                                        value={client}>
                                                                                        {({ selected, active }) => (
                                                                                            <>
                                                                                                <span
                                                                                                    className={classNames(
                                                                                                        selected ? "font-semibold" : "font-normal",
                                                                                                        "block truncate"
                                                                                                    )}>
                                                                                                    {client.name}
                                                                                                </span>

                                                                                                {selected ? (
                                                                                                    <span
                                                                                                        className={classNames(
                                                                                                            active ? "text-white" : "text-indigo-600",
                                                                                                            "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                        )}>
                                                                                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                                    </span>
                                                                                                ) : null}
                                                                                            </>
                                                                                        )}
                                                                                    </Listbox.Option>
                                                                                ))}
                                                                        </Listbox.Options>
                                                                    </Transition>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Listbox>

                                                    <Listbox disabled={!client || !client.members} value={clientMember} onChange={onMemberChange}>
                                                        {({ open }) => (
                                                            <>
                                                                <div className={"relative " + (!client || !client.members ? "opacity-30" : "")}>
                                                                    <Listbox.Button className="relative w-full min-w-[172px] cursor-default rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                                                                        <span className="block truncate">
                                                                            {!clientMember || clientMember === undefined
                                                                                ? t("select_a_client_member")
                                                                                : clientMember.id === ""
                                                                                ? t("select_a_client_member")
                                                                                : clientMember.name}
                                                                        </span>
                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                                        </span>
                                                                    </Listbox.Button>

                                                                    <Transition
                                                                        show={open}
                                                                        as={Fragment}
                                                                        leave="transition ease-in duration-100"
                                                                        leaveFrom="opacity-100"
                                                                        leaveTo="opacity-0">
                                                                        <Listbox.Options className="absolute z-10  max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                            <Listbox.Option
                                                                                className={({ active }) =>
                                                                                    classNames(
                                                                                        active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                                        "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                    )
                                                                                }
                                                                                value={undefined}>
                                                                                {({ selected, active }) => (
                                                                                    <>
                                                                                        <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                            Unassigned
                                                                                        </span>

                                                                                        {selected ? (
                                                                                            <span
                                                                                                className={classNames(
                                                                                                    active ? "text-white" : "text-indigo-600",
                                                                                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                )}>
                                                                                                <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                            </span>
                                                                                        ) : null}
                                                                                    </>
                                                                                )}
                                                                            </Listbox.Option>
                                                                            {client && client.members && client.members.length
                                                                                ? client.members.map((member: any) => (
                                                                                      <Listbox.Option
                                                                                          key={member.id}
                                                                                          className={({ active }) =>
                                                                                              classNames(
                                                                                                  active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                                                  "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                              )
                                                                                          }
                                                                                          value={member}>
                                                                                          {({ selected, active }) => (
                                                                                              <>
                                                                                                  <span
                                                                                                      className={classNames(
                                                                                                          selected ? "font-semibold" : "font-normal",
                                                                                                          "block truncate"
                                                                                                      )}>
                                                                                                      {member.name}
                                                                                                  </span>

                                                                                                  {selected ? (
                                                                                                      <span
                                                                                                          className={classNames(
                                                                                                              active ? "text-white" : "text-indigo-600",
                                                                                                              "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                          )}>
                                                                                                          <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                                      </span>
                                                                                                  ) : null}
                                                                                              </>
                                                                                          )}
                                                                                      </Listbox.Option>
                                                                                  ))
                                                                                : null}
                                                                        </Listbox.Options>
                                                                    </Transition>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Listbox>
                                                </div>
                                                <div className="flex flex-row space-x-4">
                                                    <Listbox value={engineer} onChange={setEngineer}>
                                                        {({ open }) => (
                                                            <>
                                                                <div className="relative">
                                                                    <Listbox.Button className="relative w-full min-w-[172px] cursor-default rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                                                                        <span className="block truncate">{engineer === undefined ? t("select_an_engineer") : engineer.name}</span>
                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                                        </span>
                                                                    </Listbox.Button>

                                                                    <Transition
                                                                        show={open}
                                                                        as={Fragment}
                                                                        leave="transition ease-in duration-100"
                                                                        leaveFrom="opacity-100"
                                                                        leaveTo="opacity-0">
                                                                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                            <Listbox.Option
                                                                                className={({ active }) =>
                                                                                    classNames(
                                                                                        active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                                        "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                    )
                                                                                }
                                                                                value={undefined}>
                                                                                {({ selected, active }) => (
                                                                                    <>
                                                                                        <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                            Unassigned
                                                                                        </span>

                                                                                        {selected ? (
                                                                                            <span
                                                                                                className={classNames(
                                                                                                    active ? "text-white" : "text-indigo-600",
                                                                                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                )}>
                                                                                                <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                            </span>
                                                                                        ) : null}
                                                                                    </>
                                                                                )}
                                                                            </Listbox.Option>
                                                                            {users !== undefined &&
                                                                                users.map((user: any) => (
                                                                                    <Listbox.Option
                                                                                        key={user.id}
                                                                                        className={({ active }) =>
                                                                                            classNames(
                                                                                                active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                                                "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                            )
                                                                                        }
                                                                                        value={user}>
                                                                                        {({ selected, active }) => (
                                                                                            <>
                                                                                                <span
                                                                                                    className={classNames(
                                                                                                        selected ? "font-semibold" : "font-normal",
                                                                                                        "block truncate"
                                                                                                    )}>
                                                                                                    {user.name}
                                                                                                </span>

                                                                                                {selected ? (
                                                                                                    <span
                                                                                                        className={classNames(
                                                                                                            active ? "text-white" : "text-indigo-600",
                                                                                                            "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                        )}>
                                                                                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                                    </span>
                                                                                                ) : null}
                                                                                            </>
                                                                                        )}
                                                                                    </Listbox.Option>
                                                                                ))}
                                                                        </Listbox.Options>
                                                                    </Transition>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Listbox>

                                                    <Listbox value={selected} onChange={setSelected}>
                                                        {({ open }) => (
                                                            <>
                                                                <div className="relative">
                                                                    <Listbox.Button className="relative w-full min-w-[172px] cursor-default rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                                                                        <span className="block truncate">{selected.name}</span>
                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                                        </span>
                                                                    </Listbox.Button>

                                                                    <Transition
                                                                        show={open}
                                                                        as={Fragment}
                                                                        leave="transition ease-in duration-100"
                                                                        leaveFrom="opacity-100"
                                                                        leaveTo="opacity-0">
                                                                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                            {types.map((person) => (
                                                                                <Listbox.Option
                                                                                    key={person.id}
                                                                                    className={({ active }) =>
                                                                                        classNames(
                                                                                            active ? "bg-gray-400 text-white" : "text-gray-900 dark:text-white",
                                                                                            "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                        )
                                                                                    }
                                                                                    value={person}>
                                                                                    {({ selected, active }) => (
                                                                                        <>
                                                                                            <span
                                                                                                className={classNames(
                                                                                                    selected ? "font-semibold" : "font-normal",
                                                                                                    "block truncate"
                                                                                                )}>
                                                                                                {person.name}
                                                                                            </span>

                                                                                            {selected ? (
                                                                                                <span
                                                                                                    className={classNames(
                                                                                                        active ? "text-white" : "text-indigo-600",
                                                                                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                    )}>
                                                                                                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                                </span>
                                                                                            ) : null}
                                                                                        </>
                                                                                    )}
                                                                                </Listbox.Option>
                                                                            ))}
                                                                        </Listbox.Options>
                                                                    </Transition>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Listbox>
                                                    <Listbox value={priority} onChange={setPriority}>
                                                        {({ open }) => (
                                                            <>
                                                                <div className="relative">
                                                                    <Listbox.Button className="relative w-full min-w-[172px] cursor-default rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                                                                        <span className="block capitalize truncate">{priority.name}</span>
                                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                                        </span>
                                                                    </Listbox.Button>

                                                                    <Transition
                                                                        show={open}
                                                                        as={Fragment}
                                                                        leave="transition ease-in duration-100"
                                                                        leaveFrom="opacity-100"
                                                                        leaveTo="opacity-0">
                                                                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                            {priorities.map((p) => (
                                                                                <Listbox.Option
                                                                                    key={p.id}
                                                                                    className={({ active }) =>
                                                                                        classNames(
                                                                                            active ? "bg-gray-400 text-white" : "text-gray-900 dark:text-white",
                                                                                            "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                                        )
                                                                                    }
                                                                                    value={p}>
                                                                                    {({ selected, active }) => (
                                                                                        <>
                                                                                            <span
                                                                                                className={classNames(
                                                                                                    selected ? "font-semibold" : "font-normal",
                                                                                                    "block truncate"
                                                                                                )}>
                                                                                                {p.name}
                                                                                            </span>

                                                                                            {selected ? (
                                                                                                <span
                                                                                                    className={classNames(
                                                                                                        active ? "text-white" : "text-indigo-600",
                                                                                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                                    )}>
                                                                                                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                                </span>
                                                                                            ) : null}
                                                                                        </>
                                                                                    )}
                                                                                </Listbox.Option>
                                                                            ))}
                                                                        </Listbox.Options>
                                                                    </Transition>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Listbox>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex flex-row gap-2">
                                        {!hideName && (
                                            <input
                                                type="text"
                                                placeholder={t("ticket_name_here")}
                                                name="name"
                                                value={name}
                                                readOnly={readOnly}
                                                onChange={(e) => setName(e.target.value)}
                                                className={
                                                    "relative w-full border-none rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6" +
                                                    (readOnly ? " cursor-not-allowed opacity-30" : "")
                                                }
                                            />
                                        )}

                                        {!hideEmail && (
                                            <input
                                                type="text"
                                                name="email"
                                                value={email}
                                                readOnly={readOnly}
                                                placeholder={t("ticket_email_here")}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className={
                                                    "relative w-full border-none rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6" +
                                                    (readOnly ? " cursor-not-allowed opacity-30" : "")
                                                }
                                            />
                                        )}
                                    </div>

                                    <textarea
                                        name="title"
                                        placeholder="Issue title"
                                        rows={2}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="relative mb-4 w-full border-none rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    />
                                    <div className="relative mb-4 w-full border-none rounded-md bg-white dark:bg-[#0A090C] dark:text-white py-1 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                                        <Editor setIssue={setIssue} />
                                    </div>
                                    <div>
                                        <FileUploader files={files} onFileSelect={setFiles} />
                                    </div>

                                    <div className="">
                                        <div className="mt-2 border-t border-gray-100 ">
                                            <div className="mt-3">
                                                <button
                                                    onClick={() => {
                                                        createTicket();
                                                    }}
                                                    type="button"
                                                    className="inline-flex justify-center rounded-md shadow-sm px-2.5 py-1.5 border border-transparent text-xs bg-green-600 font-medium text-white hover:bg-green-700 focus:outline-none ">
                                                    Create Ticket
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}
